import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  Flex,
  Link,
} from "@chakra-ui/react";
import { PrenotazioneDetails } from "../types/Booking";
import { getUserInfo, ClientPrincipal } from "../authConfig";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const CancelReservation: React.FC = () => {
  const { prenotazioneId } = useParams<{ prenotazioneId: string }>();
  const navigate = useNavigate();
  const [prenotazione, setPrenotazione] = useState<PrenotazioneDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [user, setUser] = useState<ClientPrincipal | null>(null);

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      const userInfo = await getUserInfo();
      setUser(userInfo);
    };
    checkAuth();
  }, []);

  const backPath = user ? "/le-mie-prenotazioni" : "/";

  useEffect(() => {
    const fetchPrenotazione = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/prenotazioni/${prenotazioneId}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError("Prenotazione non trovata");
          } else {
            setError("Errore nel recupero della prenotazione");
          }
          return;
        }
        const data = await response.json();
        setPrenotazione(data);
        if (data.Annullata) {
          setCancelled(true);
        }
      } catch (err) {
        setError("Errore di connessione");
      } finally {
        setLoading(false);
      }
    };

    fetchPrenotazione();
  }, [prenotazioneId]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const response = await fetch(`${API_BASE_URL}/prenotazioni/${prenotazioneId}`, {
        method: "DELETE",
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        setError(errorText);
        return;
      }
      
      setCancelled(true);
    } catch (err) {
      setError("Errore nell'annullamento della prenotazione");
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Modal-like container
  const ModalContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <>
      <Box position="fixed" top={0} left={0} right={0} bottom={0} bg="blackAlpha.900" backdropFilter="blur(8px)" zIndex={100} />
      <Flex position="fixed" top={0} left={0} right={0} bottom={0} zIndex={101} align="center" justify="center" p={4}>
        <Box 
          bg="gray.900" 
          w="full" 
          maxW="xl" 
          maxH="85vh"
          overflowY="auto"
          border="1px solid" 
          borderColor="whiteAlpha.200"
          display="flex"
          flexDirection="column"
        >
          {children}
        </Box>
      </Flex>
    </>
  );

  // Header component
  const Header: React.FC<{ subtitle: string; title?: string }> = ({ subtitle, title }) => (
    <Box pt={8} pb={4} px={6} flexShrink={0}>
      <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="widest" mb={title ? 2 : 0}>
        {subtitle}
      </Text>
      {title && (
        <Text fontSize="xl" fontWeight="400" color="white" textTransform="uppercase" letterSpacing="wide">
          {title}
        </Text>
      )}
    </Box>
  );

  // Footer component
  const Footer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <HStack gap={3} p={6} borderTop="1px solid" borderColor="whiteAlpha.200" flexShrink={0} justify="center">
      {children}
    </HStack>
  );

  if (loading) {
    return (
      <ModalContainer>
        <VStack py={20} spacing={4}>
          <Spinner size="xl" color="white" thickness="3px" />
          <Text color="gray.400" textTransform="uppercase" letterSpacing="wide" fontSize="sm">
            Caricamento...
          </Text>
        </VStack>
      </ModalContainer>
    );
  }

  if (error) {
    return (
      <ModalContainer>
        <Header subtitle="ERRORE" />
        <VStack spacing={6} p={6} flex={1}>
          <Box
            w="80px"
            h="80px"
            borderRadius="full"
            border="2px solid"
            borderColor="red.400"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="3xl" color="red.400">✕</Text>
          </Box>
          <Text color="gray.400" textAlign="center">{error}</Text>
        </VStack>
        <Footer>
          <Button 
            as={RouterLink}
            to={backPath}
            bg="transparent" 
            color="white" 
            border="1px solid" 
            borderColor="whiteAlpha.300" 
            borderRadius="none"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="sm"
            _hover={{ bg: "white", color: "gray.900" }}
          >
            INDIETRO
          </Button>
        </Footer>
      </ModalContainer>
    );
  }

  if (cancelled) {
    return (
      <ModalContainer>
        <Header subtitle="CONFERMA" />
        <VStack spacing={6} p={6} flex={1}>
          <Box
            w="80px"
            h="80px"
            borderRadius="full"
            border="2px solid"
            borderColor="green.400"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="3xl" color="green.400">✓</Text>
          </Box>
          <Text
            fontSize="xl"
            fontWeight="500"
            color="white"
            textTransform="uppercase"
            letterSpacing="widest"
            textAlign="center"
          >
            PRENOTAZIONE ANNULLATA
          </Text>
          <Text
            fontSize="xs"
            color="gray.400"
            textTransform="uppercase"
            letterSpacing="wide"
            textAlign="center"
          >
            La tua prenotazione è stata annullata con successo
          </Text>
        </VStack>
        <Footer>
          <Button 
            as={RouterLink}
            to={backPath}
            bg="white" 
            color="gray.900" 
            borderRadius="none"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="sm"
            _hover={{ bg: "gray.200" }}
          >
            INDIETRO
          </Button>
        </Footer>
      </ModalContainer>
    );
  }

  if (!prenotazione) {
    return null;
  }

  const subtotale = 
    (prenotazione.NumeroAlcolico * (prenotazione.PrezzoAlcolico || 0)) +
    (prenotazione.NumeroAnalcolico * (prenotazione.PrezzoAnalcolico || 0));
  
  const giftCardDiscount = prenotazione.GiftCardCodice 
    ? ((prenotazione.GiftCardNumeroAlcolico || 0) * (prenotazione.PrezzoAlcolico || 0)) + 
      ((prenotazione.GiftCardNumeroAnalcolico || 0) * (prenotazione.PrezzoAnalcolico || 0))
    : 0;
  
  const totale = subtotale - giftCardDiscount;

  const eventDate = new Date(prenotazione.DataEvento);
  const now = new Date();
  const isPast = eventDate < now;
  
  // Check if event is within 72 hours
  const hoursUntilEvent = (eventDate.getTime() - now.getTime()) / (1000 * 60 * 60);
  const isTooNear = hoursUntilEvent > 0 && hoursUntilEvent <= 72;

  // If event is too near, show contact message
  if (isTooNear) {
    return (
      <ModalContainer>
        <Header subtitle="ANNULLA PRENOTAZIONE" title={prenotazione.EventoTitolo} />
        <VStack spacing={6} p={6} flex={1} align="stretch">
          <Box
            w="80px"
            h="80px"
            borderRadius="full"
            border="2px solid"
            borderColor="orange.400"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mx="auto"
          >
            <Text fontSize="3xl" color="orange.400">!</Text>
          </Box>
          <Text
            fontSize="lg"
            fontWeight="500"
            color="white"
            textTransform="uppercase"
            letterSpacing="widest"
            textAlign="center"
          >
            EVENTO TROPPO VICINO
          </Text>
          <Text
            fontSize="xs"
            color="gray.400"
            textTransform="uppercase"
            letterSpacing="wide"
            textAlign="center"
          >
            Non è possibile annullare online una prenotazione per un evento che si svolgerà entro le prossime 72 ore.
          </Text>

          <Box w="full" h="1px" bg="whiteAlpha.200" />

          <Box bg="whiteAlpha.50" p={5} border="1px solid" borderColor="whiteAlpha.200">
            <Text fontSize="lg" fontWeight="light" letterSpacing="widest" textTransform="uppercase" color="white">{prenotazione.EventoTitolo}</Text>
            <Text color="gray.400" fontSize="xs" mt={2} textTransform="uppercase" letterSpacing="wide">
              {formatDate(prenotazione.DataEvento)}
            </Text>
          </Box>

          <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="blue.500">
            <Text fontWeight="medium" mb={3} textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="white">
              PER ANNULLARE, CONTATTACI:
            </Text>
            <VStack align="start" spacing={2}>
              <HStack>
                <Text color="gray.400" fontSize="sm">📧</Text>
                <Link href="mailto:info@scarpettahomerestaurant.it" color="blue.300" fontWeight="medium" fontSize="sm">
                  info@scarpettahomerestaurant.it
                </Link>
              </HStack>
              <HStack>
                <Text color="gray.400" fontSize="sm">📞</Text>
                <Link href="tel:+393517934768" color="blue.300" fontWeight="medium" fontSize="sm">
                  +39 351 793 4768
                </Link>
              </HStack>
            </VStack>
          </Box>
        </VStack>
        <Footer>
          <Button 
            as={RouterLink}
            to={backPath}
            bg="transparent" 
            color="white" 
            border="1px solid" 
            borderColor="whiteAlpha.300" 
            borderRadius="none"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="sm"
            _hover={{ bg: "white", color: "gray.900" }}
          >
            INDIETRO
          </Button>
        </Footer>
      </ModalContainer>
    );
  }

  return (
    <ModalContainer>
      <Header subtitle="ANNULLA PRENOTAZIONE" title={prenotazione.EventoTitolo} />
      <VStack spacing={6} p={6} flex={1} align="stretch">
        <Box
          w="80px"
          h="80px"
          borderRadius="full"
          border="2px solid"
          borderColor="orange.400"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mx="auto"
        >
          <Text fontSize="3xl" color="orange.400">?</Text>
        </Box>
        <Text
          fontSize="lg"
          fontWeight="500"
          color="white"
          textTransform="uppercase"
          letterSpacing="widest"
          textAlign="center"
        >
          CONFERMA ANNULLAMENTO
        </Text>
        <Text
          fontSize="xs"
          color="gray.400"
          textTransform="uppercase"
          letterSpacing="wide"
          textAlign="center"
        >
          Sei sicuro di voler annullare questa prenotazione?
        </Text>

        <Box w="full" h="1px" bg="whiteAlpha.200" />

        <Box bg="whiteAlpha.50" p={5} border="1px solid" borderColor="whiteAlpha.200">
          <Text fontSize="lg" fontWeight="light" letterSpacing="widest" textTransform="uppercase" color="white">{prenotazione.EventoTitolo}</Text>
          <Text color="gray.400" fontSize="xs" mt={2} textTransform="uppercase" letterSpacing="wide">
            {formatDate(prenotazione.DataEvento)}
          </Text>
        </Box>

        <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">INTESTATARIO</Text>
              <Text fontWeight="medium" color="white" textTransform="uppercase" letterSpacing="wide">{prenotazione.Nome} {prenotazione.Cognome}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">PARTECIPANTI</Text>
              <Text color="white">{prenotazione.NumeroPersone}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">MENU ALCOLICO</Text>
              <Text color="white">{prenotazione.NumeroAlcolico}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">MENU ANALCOLICO</Text>
              <Text color="white">{prenotazione.NumeroAnalcolico}</Text>
            </HStack>
            {prenotazione.GiftCardCodice && (
              <>
                <Box w="full" h="1px" bg="whiteAlpha.200" my={1} />
                <HStack justify="space-between">
                  <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">SUBTOTALE</Text>
                  <Text color="white">{formatCurrency(subtotale)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">GIFT CARD ({prenotazione.GiftCardCodice})</Text>
                  <Text color="green.400">-{formatCurrency(giftCardDiscount)}</Text>
                </HStack>
              </>
            )}
            <Box w="full" h="1px" bg="whiteAlpha.200" my={1} />
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">TOTALE</Text>
              <Text fontWeight="medium" color="white">{formatCurrency(totale)}</Text>
            </HStack>
          </VStack>
        </Box>

        {isPast && (
          <Alert status="warning" borderRadius="none" bg="whiteAlpha.100" border="1px solid" borderColor="orange.500">
            <AlertIcon color="orange.400" />
            <Text fontSize="xs" color="white" textTransform="uppercase" letterSpacing="wide">
              Non è possibile annullare una prenotazione per un evento già passato
            </Text>
          </Alert>
        )}
      </VStack>
      <Footer>
        <Button 
          onClick={() => navigate(backPath)}
          bg="transparent" 
          color="gray.400" 
          borderRadius="none"
          textTransform="uppercase"
          letterSpacing="widest"
          fontSize="sm"
          _hover={{ color: "white" }}
        >
          ANNULLA
        </Button>
        {!isPast && (
          <Button 
            onClick={handleCancel}
            isLoading={cancelling}
            loadingText="ANNULLAMENTO..."
            bg="red.500" 
            color="white" 
            borderRadius="none"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="sm"
            _hover={{ bg: "red.600" }}
          >
            CONFERMA ANNULLAMENTO
          </Button>
        )}
      </Footer>
    </ModalContainer>
  );
};

export default CancelReservation;
